import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Icon, Loading, SidePanel, Typography } from "@/components/atoms";
import { ClientHeader } from "@/components/organisms/ClientHeader";
import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import { clearStashedGuestDraft, readStashedGuestDraft } from "@/components/pages/Guest/guestDraftStash";
import { useHydrateBookingFromQuote } from "@/components/pages/Guest/useHydrateBookingFromQuote";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { getTripDetailErrors } from "@/redux/slices/clientBooking/bookingPayload";
import { initBookingScope, restoreBookingDraft } from "@/redux/slices/clientBooking/clientBookingSlice";
import { selectClientBookingDraft, selectDepartureFlightNumber, selectPickupFlightNumber } from "@/redux/slices/clientBooking/selectors";
import { clsx } from "@/utils";
import { BookingAttachmentsProvider } from "./booking/BookingAttachmentsContext";
import { MapHandle } from "@/components/atoms/Map/Map";
import { BookingMapBackground } from "./booking/BookingMapBackground";
import { BookingViewOnlyProvider } from "./booking/BookingViewOnlyContext";
import { BookingSheetBanner } from "./booking/BookingSheetBanner";
import { useSheetDepartureFlight } from "./booking/hooks/useSheetDepartureFlight";
import { useSheetFlightPickup } from "./booking/hooks/useSheetFlightPickup";
import { useHydratedBooking } from "./booking/useHydratedBooking";
import { BookingSubHeader } from "./booking/BookingSubHeader";
import { BookingStep, BookingSummaryPanel } from "./booking/BookingSummaryPanel";
import { LeaveBookingModal } from "./booking/LeaveBookingModal";
import { LocationPanel } from "./booking/LocationPanel";
import { PaymentPanel } from "./booking/PaymentPanel";
import { TripDetailSubPanel, TripDetailSubPanelSheet } from "./booking/TripDetailSubPanel";
import { TRIP_DETAIL_ORDER, TripDetailItem, TripDetailsPanel, nextTripDetail } from "./booking/TripDetailsPanel";
import { useBookedByPrefill } from "./booking/useBookedByPrefill";
import { useClientScope } from "./settings/ClientScopeContext";

/**
 * Per-step width for the floating sub-panel (md+ / @[768px] card layout only). Date/Time is special-
 * cased below: 408px when the flight-details layout is showing (an airport pickup with a captured
 * flight number, Kevin's flight-integration spec), else the narrower 300px regular date/time card.
 * The fluid calendar grid is comfortable at both. The Trip Details list stays 400px.
 */
/** Wizard order — used to track the furthest step reached (see `furthest` below). */
const STEP_ORDER: BookingStep[] = ["address", "trip-details", "payment"];
const stepRank = (step: BookingStep) => STEP_ORDER.indexOf(step);

const SUBPANEL_WIDTH: Record<TripDetailItem, string> = {
  passenger: "@[768px]:w-[400px]",
  datetime: "@[768px]:w-[300px]",
  vehicle: "@[768px]:w-[496px]",
  other: "@[768px]:w-[400px]",
};

/**
 * Client booking sheet. The current step (`address` → `trip-details` → `payment`) is local component
 * state — the wizard is linear and gated, so there's no need to reflect it in the URL.
 *
 * Breakpoints are @container queries against the sheet's own width (not the viewport), so the
 * layout stays correct whether it fills the client portal (no sidebar) or the operator shell
 * (270px sidebar constrains the width) — the repo convention for width-constrained reuse.
 *
 * wide (@[1024px]): floating step panel(s) over a full-bleed map + persistent Booking Summary sidebar (355px).
 * < 1024px:  collapsed header (from ClientHeader) + a sub-header bar; the Booking Summary is surfaced
 *            as a full-width slide-in panel (SidePanel) via "View Booking Summary".
 * mobile: the step panel becomes a bottom-sheet card.
 */
/**
 * Remount the whole sheet whenever the booking identity changes (create ↔ a specific job, or between
 * jobs) by keying on `:jobId`. Since the wizard step is now local state (no `?step=`) and React reuses
 * the same element across the `booking` and `booking/:jobId` routes, without this key navigating from
 * an edit back to a fresh "Book a Trip" would keep the old step AND the pre-filled draft. A fresh mount
 * resets the step to Address and re-runs useHydratedBooking, which clears any replayed remnant.
 */
export const ClientBooking = () => {
  const { jobId } = useParams<{ jobId?: string }>();
  return <ClientBookingSheet key={jobId ?? "new"} />;
};

const ClientBookingSheet = () => {
  const [showSummary, setShowSummary] = useState(false);
  // Still read for the guest/self-service `quote_uuid` (+ `widget`) hand-off; no longer written to.
  const [searchParams] = useSearchParams();

  const dispatch = useAppDispatch();

  const { accountId } = useClientScope();

  // Replay of an existing booking when the route carries a :jobId. Mode (view vs edit) is decided by
  // the booking's own `is_editable`, resolved after the detail loads — so during load we only know
  // it's a replay (isReplay), not yet which mode.
  const {
    isViewOnly,
    isEdit,
    isReplay,
    isLoading: replayLoading,
    error: replayError,
    retry: retryHydration,
    displayJobId,
    notEditableReason,
  } = useHydratedBooking();
  const navigate = useNavigate();
  // Status banner atop the sheet/summary: silver "view-only" for a locked booking, info-blue
  // "Editing booking J…" for an edit. BOTH name the job, so a client can tell which of their bookings
  // the bar is about. Same banner rendered in the main column (tablet/mobile), the desktop summary
  // sidebar, and the slide-in summary.
  const sheetBanner = (bannerClassName?: string) =>
    isViewOnly ? (
      <BookingSheetBanner mode="view" jobLabel={displayJobId} notEditableReason={notEditableReason} className={bannerClassName} />
    ) : isEdit ? (
      <BookingSheetBanner mode="edit" jobLabel={displayJobId} className={bannerClassName} />
    ) : null;

  // Bind the draft to the current scope. If the sheet is entered for a different client (or
  // switched between operator OBO and self-service), the draft resets so one account's
  // locations/passenger/payment never leak into another's. Re-entering the same scope is a
  // no-op, so an in-progress draft survives incidental navigation within the same booking.
  useEffect(() => {
    dispatch(initBookingScope(accountId ?? null));
  }, [accountId, dispatch]);

  // Resume: if a guest hit ACCOUNT_EXISTS (409) and logged in, restore the stashed draft so
  // the booking continues where it left off. Runs AFTER initBookingScope (which resets the draft) so
  // the restore wins. No-op for normal authed users — there's no stash — so the authed flow is unchanged.
  const resumedRef = useRef(false);
  // Drives the map's +/- zoom buttons (the map hides Google's default controls).
  const mapControlRef = useRef<MapHandle>(null);
  useEffect(() => {
    if (resumedRef.current) return;
    const stashed = readStashedGuestDraft();
    if (!stashed) return;
    resumedRef.current = true;
    dispatch(restoreBookingDraft(stashed));
    clearStashedGuestDraft();
  }, [dispatch]);

  const [step, setStep] = useState<BookingStep>("address");

  // Furthest step the user has reached. The wizard is linear and gated (you can't reach Trip Details
  // without a full route, nor Payment without passing the Trip Details exit gate), so being on a step
  // proves every earlier step was completed. Never regresses — editing an earlier step keeps the
  // later ones "gone through".
  const [furthest, setFurthest] = useState<BookingStep>(step);
  useEffect(() => {
    setFurthest((prev) => (stepRank(step) > stepRank(prev) ? step : prev));
  }, [step]);
  const enteredTripDetails = stepRank(furthest) >= stepRank("trip-details");
  // Steps the user has gone through — derived from the furthest step, fed to the summary's edit
  // pencils + populated boxes. Address is done once Trip Details is reached; Trip Details + Payment
  // once Payment is reached (Payment is the last step, so reaching it counts as having gone through it —
  // that's what lets the draft summary keep showing the chosen card + an edit pencil after navigating back).
  const completedSteps: BookingStep[] = [];
  if (stepRank(furthest) >= stepRank("trip-details")) completedSteps.push("address");
  if (stepRank(furthest) >= stepRank("payment")) completedSteps.push("trip-details", "payment");
  // View/edit replay: every step is "gone through" so the summary shows all boxes + edit pencils and
  // the user can freely page through the fully-populated booking.
  const effectiveCompleted = isReplay ? (STEP_ORDER as BookingStep[]) : completedSteps;

  // Prefill "Booked by" so Other Details is complete (and shows in the summary) without an explicit
  // Proceed — its remaining fields are optional. Only seeds while empty, so panel edits are kept.
  // Gated on entering Trip Details so the summary isn't pre-populated on the Address step.
  useBookedByPrefill(enteredTripDetails);

  // Resolve the booking's flight for the life of the SHEET, not just while the Date/Time sub-panel happens
  // to be mounted. Called here for the subscription alone — held at the root so the flight is already
  // resolved whichever step the client came to change, and survives every panel mount/unmount in between.
  // `PricedBookingSummary` calls the same hook to re-derive the pickup on save, off this same cache entry.
  // The terminal swap is driven from HERE and only here — this is the one mount that spans the sheet, and
  // the effect must fire once per resolved flight rather than once per mounted instance.
  useSheetFlightPickup({ syncPickupTerminal: true });
  // The DROP-OFF twin (DEV-1487) — resolves the departure flight for the life of the sheet and swaps the
  // drop-off to the terminal it leaves from. Mounted here and only here for the same reason: one mount
  // spans the sheet, and the swap must fire once per resolved flight rather than once per mounted panel.
  useSheetDepartureFlight();

  const goToStep = (next: BookingStep) => setStep(next);

  // Which Trip Details row is expanded into the sub-panel. On mobile the sub-panel replaces the
  // list, so `mobileDetailOpen` tracks whether we're viewing the list or the sub-panel.
  const [activeItem, setActiveItem] = useState<TripDetailItem>("passenger");
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  // Desktop-only sub-panel visibility, consulted ONLY in edit mode. Create keeps its always-open card
  // (the passenger sub-panel shows the moment you reach Trip Details); an edit opens with NOTHING
  // selected and each row toggles this open, so the user picks the one field they want to change.
  const [detailOpen, setDetailOpen] = useState(false);
  // Edit only: true while the desktop card plays its slide-out keyframe on Confirm, before it unmounts.
  const [detailClosing, setDetailClosing] = useState(false);

  // Whole-set validation for the Trip Details step. Each sub-panel already validates its own fields
  // on Proceed (Layer 1); this is the exit gate (Layer 2) that catches sub-steps the user skipped by
  // row-jumping and never committing. Rows stay neutral until the user actually tries to leave the
  // step (`attemptedNext`), then every incomplete row lights up danger and clears as it's fixed.
  const { isGuest } = useGuestBooking();

  // Self-service only: a logged-in client arriving from a widget quote lands here with a `quote_uuid`.
  // Gated on no guest context + no managed-client scope so guest/OBO (which prefill in their own
  // wrappers) don't double-hydrate; no `quote_uuid` is a no-op for a normal booking.
  const selfServiceQuoteUuid = !isGuest && !accountId ? searchParams.get("quote_uuid") ?? undefined : undefined;
  useHydrateBookingFromQuote({ quoteUuid: selfServiceQuoteUuid, resolveOperator: true });
  const bookingDraft = useAppSelector(selectClientBookingDraft);
  const tripDetailErrors = getTripDetailErrors(bookingDraft, isGuest);
  const [attemptedNext, setAttemptedNext] = useState(false);
  // When true, the currently-open sub-panel runs its own field validation on mount so the user sees
  // the inline errors right where the fix is (not just the danger row). Set only when a failed exit
  // jumps them into a panel; cleared on any manual navigation so it never fires on a deliberate visit.
  const [autoValidate, setAutoValidate] = useState(false);
  const tryLeaveTripDetails = () => {
    const firstBad = TRIP_DETAIL_ORDER.find((item) => tripDetailErrors[item].length > 0);
    if (firstBad) {
      setAttemptedNext(true);
      setActiveItem(firstBad);
      setAutoValidate(true);
      setMobileDetailOpen(true); // mobile: surface the offending sub-panel
      setDetailOpen(true); // edit desktop: open the offending sub-panel so its inline errors are visible
      return;
    }
    goToStep("payment");
  };

  // Date/Time widens to fit the flight-details card; otherwise it's the regular narrow date/time card.
  // EITHER flight earns the width (DEV-1619): the panel renders the same `FlightDetailsCard` for a
  // departure as for an arrival, so keying on the arrival alone left an airport DROP-OFF booking drawing
  // that card into the narrow 300px panel. Both numbers are captured on the Address step, so either is
  // already known when the panel opens — the width won't jump mid-render.
  const pickupFlightNumber = useAppSelector(selectPickupFlightNumber);
  const departureFlightNumber = useAppSelector(selectDepartureFlightNumber);
  const subPanelWidth =
    activeItem === "datetime" && (pickupFlightNumber || departureFlightNumber) ? "@[768px]:w-[408px]" : SUBPANEL_WIDTH[activeItem];
  const selectItem = (item: TripDetailItem) => {
    setActiveItem(item);
    setAutoValidate(false); // a deliberate visit shouldn't pre-fire the panel's error display
    setMobileDetailOpen(true);
    setDetailOpen(true); // edit desktop: a row click opens its sub-panel (create's card is always open)
    setDetailClosing(false); // cancel any in-flight slide-out if they re-open mid-close
  };
  /**
   * A changed flight on an EDIT sends the client to Date & Time (DEV-1619) — EITHER leg, for the same
   * reason by two different routes. The reasoning below is the ARRIVAL's, which owns the pickup outright.
   *
   * The DEPARTURE reaches here through a narrower door: it never derives the pickup (see
   * `useSheetDepartureFlight`, which commits nothing), it only re-opens the SUGGESTED pickup that
   * `setDepartureFlightNumber` withdraws. So none of the arrival's machinery below applies to it — no
   * save-time commit, nothing for the change-review modal to disclose — and `LocationPanel` stays silent
   * on a drop-off change whenever an arrival flight owns the pickup, because then the suggestion is
   * structurally withdrawn and there would be nothing here to see.
   *
   * The pickup re-derives from the new flight either way — `useSheetFlightPickup` commits it on save and
   * the change-review modal discloses the move. But disclosure at the moment of saving is the wrong moment
   * to FIRST learn that the time moved: the client changed a flight, and the time is the consequence they
   * came to see. Landing them on the panel that shows the flight card and the derived pickup makes the new
   * time something they read, not something they are told about on the way out.
   *
   * It also covers the case the re-derivation cannot: a replacement that does not operate on the booked
   * date resolves no landing, so there is nothing to re-derive and the pickup would silently stay on the
   * REPLACED flight's time. This panel is where the date is editable and where "we couldn't find flight X
   * on that date" is already rendered, so the one path with no automatic answer lands where the manual one
   * is.
   *
   * EDIT ONLY. On create the wizard walks every booking through this step anyway, and the first flight
   * captured for an airport pickup is a change from `null` — so wiring it there would yank the client to
   * Date & Time the moment they picked an airport, skipping Passenger.
   */
  const showPickupTimeForNewFlight = () => {
    goToStep("trip-details");
    selectItem("datetime");
  };

  // Auto-advance: after a sub-step's save/proceed succeeds, open the next one in order. From the
  // last sub-step ("other") there's no next panel, so Proceed moves the wizard on to Payment
  // (mirrors the list's "Next Step"). Manual row clicks (selectItem) still let the user jump around.
  const advanceItem = () => {
    // Edit mode: "Confirm" just commits the field and CLOSES the sub-panel — it never auto-advances to
    // the next one. The user updates one field at a time, then hits "Update Booking" from the summary.
    // Desktop: play the slide-out keyframe first, then unmount once it finishes (matches the 220ms anim).
    // Mobile: the bottom sheet closes immediately (its own open/close handles the motion).
    if (isEdit) {
      setMobileDetailOpen(false);
      setAutoValidate(false);
      setDetailClosing(true);
      window.setTimeout(() => {
        setDetailOpen(false);
        setDetailClosing(false);
      }, 220);
      return;
    }
    const next = nextTripDetail(activeItem);
    // Last sub-step ("other"): its own Proceed already ran the panel's field validation, so run the
    // whole-set exit gate rather than navigating straight to Payment.
    if (next === activeItem) tryLeaveTripDetails();
    else {
      setActiveItem(next);
      setAutoValidate(false); // a clean auto-advance opens the next panel fresh, not in an error state
    }
  };

  return (
    <BookingViewOnlyProvider value={isViewOnly}>
    <BookingAttachmentsProvider>
    <div className="relative flex min-h-0 flex-1 @container">
      <LeaveBookingModal />
      {/* View/edit hydrate the whole draft from the booking detail; cover the sheet until it lands so
          the panels + summary don't flash empty. */}
      {isReplay && replayLoading && <Loading className="z-30" />}
      {/* Hydration failed — a recoverable error instead of a permanent spinner (the sheet has no draft to
          show, so cover it with a retry / go-back panel). */}
      {isReplay && replayError && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-white px-6 text-center">
          <Icon name="InfoCircle" size="xl" className="text-danger" />
          <Typography variant="title">We couldn&apos;t load this booking</Typography>
          <Typography variant="paragraph" className="max-w-sm text-neutral-dark-gray">
            {replayError}
          </Typography>
          <div className="flex gap-3">
            <Button variant="tertiary" size="md" onClick={() => navigate(-1)}>
              Go back
            </Button>
            <Button variant="primary" size="md" onClick={retryHydration}>
              Try again
            </Button>
          </div>
        </div>
      )}
      {/* Main column: sub-header (small screens) + map content */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* View/edit banner for tablet/mobile: the Booking Summary (which carries the banner on desktop)
            is a tap-to-open slide-in here, so surface the banner above the sub-header instead. Hidden at
            @[1024px]+ where the persistent summary sidebar already shows it. */}
        {sheetBanner("@[1024px]:hidden")}
        <BookingSubHeader
          className="@[1024px]:hidden"
          title="Booking Sheet"
          actionLabel="View Booking Summary"
          actionIcon="ArrowRight"
          actionIconPosition="end"
          actionIconClassName="hidden @[768px]:block"
          onAction={() => setShowSummary(true)}
        />

        <div className="relative min-h-0 flex-1">
          <BookingMapBackground mapControlRef={mapControlRef} />

          {/* Map zoom control — wider sheets only (the map hides Google's default UI). */}
          <div className="absolute right-4 top-4 hidden flex-col overflow-hidden rounded-lg border border-neutral-mid-gray bg-white shadow @[768px]:flex">
            <button
              type="button"
              aria-label="Zoom in"
              onClick={() => mapControlRef.current?.zoomIn()}
              className="flex h-8 w-8 items-center justify-center text-neutral-dark-gray hover:bg-neutral-surface-gray"
            >
              <Icon name="Add" size="sm" />
            </button>
            <button
              type="button"
              aria-label="Zoom out"
              onClick={() => mapControlRef.current?.zoomOut()}
              className="flex h-8 w-8 items-center justify-center border-t border-neutral-mid-gray text-neutral-dark-gray hover:bg-neutral-surface-gray"
            >
              <Icon name="Minus" size="sm" />
            </button>
          </div>

          {step === "address" ? (
            // Mobile: content-height card near the bottom, map visible above (the focused state is a
            // full-screen takeover, so the card never needs to grow). md+: top+bottom anchored column;
            // 32px bottom gap clears Google's logo/attribution.
            <LocationPanel
              className="absolute inset-x-2.5 bottom-8 max-h-[calc(100%-2.5rem)] @[768px]:right-auto @[768px]:top-2.5 @[768px]:w-[400px]"
              onNext={() => goToStep("trip-details")}
              onFlightChanged={isEdit ? showPickupTimeForNewFlight : undefined}
            />
          ) : step === "payment" ? (
            <PaymentPanel
              // Mobile: full-screen white page over the map (like Trip Details, per the mobile Figma);
              // wider: floating card over the map like the other step panels.
              className="absolute inset-0 @[768px]:inset-auto @[768px]:bottom-8 @[768px]:left-2.5 @[768px]:top-2.5 @[768px]:w-[400px]"
              onPrevious={() => goToStep("trip-details")}
              onViewSummary={() => setShowSummary(true)}
            />
          ) : (
            <>
              {/* Trip Details list + sub-panel. Mobile: the list fills the whole screen (plain white page,
                  covering the map); the sub-panel is a bottom sheet below. md+: they float side-by-side as
                  cards over the map — top-2.5/bottom-8 like the Address panel (32px bottom clears Google's logo).
                  pointer-events-none lets the map stay draggable through the wrapper's empty area (e.g. below
                  the shorter sub-panel); the panels re-enable events. */}
              <div className="pointer-events-none absolute inset-0 flex @[768px]:inset-auto @[768px]:bottom-8 @[768px]:left-2.5 @[768px]:top-2.5 @[768px]:items-stretch @[768px]:gap-2.5">
                <TripDetailsPanel
                  // Edit opens with nothing selected (no row highlighted) until the user picks one;
                  // create always has the active row highlighted (its card is always open).
                  active={!isEdit || detailOpen ? activeItem : null}
                  onSelect={selectItem}
                  onPrevious={() => goToStep("address")}
                  // TODO(booking-submit): re-validate committed pickup freshness via validatePickup()
                  // from booking/pickupTime at Book Trip time — a long-open tab can make a once-valid
                  // pickup stale (submit lives in PricedBookingSummary, not here).
                  // Replay (view/edit) opens fully populated, so skip the exit-gate re-validation and
                  // page straight on; the summary's CTA still enforces validity before update/book.
                  onNext={isReplay ? () => goToStep("payment") : tryLeaveTripDetails}
                  errors={tripDetailErrors}
                  showErrors={attemptedNext && !isReplay}
                  className="pointer-events-auto w-full @[768px]:w-[400px]"
                />
                {/* Sub-panel sizes to its content, but never taller than the Trip Details panel
                    (md:max-h-full) — past that it scrolls internally. md:self-start opts it out of
                    the row's stretch so short content stays short. Width is per-step (SUBPANEL_WIDTH)
                    and fixed within a step, so the Date/Time card doesn't jump once flight details render.
                    Edit mode: hidden until the user opens a row (create keeps it always open). */}
                {(!isEdit || detailOpen) && (
                  <TripDetailSubPanel
                    // Remount per selection so the panel slides in from the side each time a card is
                    // clicked (visually tying the details to the row).
                    key={activeItem}
                    item={activeItem}
                    onAdvance={advanceItem}
                    onUpdateFlight={() => goToStep("address")}
                    forceValidate={autoValidate}
                    closing={detailClosing}
                    className={clsx("pointer-events-auto hidden @[768px]:flex @[768px]:max-h-full @[768px]:self-start", subPanelWidth)}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile: tapping a row opens the sub-panel as a bottom sheet. It portals to <body> as a
          full-viewport modal, so its dimmed backdrop covers the header too (per Figma). */}
      {step === "trip-details" && (
        <TripDetailSubPanelSheet
          className="md:hidden"
          item={activeItem}
          open={mobileDetailOpen}
          forceValidate={autoValidate}
          onClose={() => setMobileDetailOpen(false)}
          onProceed={() => setMobileDetailOpen(false)}
          onUpdateFlight={() => {
            setMobileDetailOpen(false);
            goToStep("address");
          }}
        />
      )}

      {/* Persistent Booking Summary sidebar — only when the sheet is wide enough (@[1024px]). 40px top; the
          20px side padding is applied inside the panel (via edgeClassName) so the Payment step's sticky
          footer + its divider can span the panel's full width. */}
      <aside className="hidden w-[355px] shrink-0 overflow-auto border-l border-neutral-mid-gray bg-white @[1024px]:block">
        {sheetBanner()}
        <div className={clsx(isViewOnly || isEdit ? "pt-4" : "pt-10")}>
          <BookingSummaryPanel activeStep={step} completedSteps={effectiveCompleted} onEditStep={goToStep} edgeClassName="px-5" />
        </div>
      </aside>

      {/* Tablet/mobile: Booking Summary as a full-width slide-in panel.
          Custom header (h2 title + sm Back button w/ left arrow) + body, so it keeps the approved design.
          Padding: 10px top/bottom (py-2.5), 20px sides on mobile (px-5), 60px sides on tablet (md:px-[60px]). */}
      <div className="@[1024px]:hidden">
        {/* Two children (header + body) so SidePanel renders our custom header instead of its default
            close-"x" bar. The header is the app's main mobile header (operator logo + name + hamburger)
            — the "x" was redundant with the "Back" action below it. ClientHeader supplies its own bottom
            border, so suppress SidePanel's divider to avoid a double line. */}
        <SidePanel
          open={showSummary}
          onClose={() => setShowSummary(false)}
          focused
          hideDivider
          width={typeof window !== "undefined" ? window.innerWidth : 400}
        >
          <ClientHeader />
          <>
          {sheetBanner()}
          <div className="flex items-center justify-between px-5 py-2.5 md:px-[60px]">
            <Typography variant="h2">Booking Summary</Typography>
            <button type="button" onClick={() => setShowSummary(false)} className="flex items-center gap-1.5 text-primary">
              <Icon name="ArrowLeft" size="sm" />
              <Typography variant="action">Back</Typography>
            </button>
          </div>
          {/* h-full keeps the height chain intact (SidePanel's flex-1 scroll body → here → the panel's
              min-h-full) so the Payment step's sticky "Book Trip" footer pins to the bottom instead of
              floating with dead space beneath it. Side padding lives inside the panel (edgeClassName) so
              the sticky footer + divider span the full width; 10px top here, bottom owned by the panel. */}
          <div className="h-full pt-2.5">
            <BookingSummaryPanel
              hideHeading
              activeStep={step}
              completedSteps={effectiveCompleted}
              edgeClassName="px-5 md:px-[60px]"
              onEditStep={(next) => {
                // Close the slide-in first so the target step's panel is visible underneath.
                setShowSummary(false);
                goToStep(next);
              }}
            />
          </div>
          </>
        </SidePanel>
      </div>
    </div>
    </BookingAttachmentsProvider>
    </BookingViewOnlyProvider>
  );
};
